/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import type {
  CloudAccountsFilterInput,
  CloudAccountsSearchInput,
} from "~/generated/graphql";
import {
  CloudAccountsFilterableFields,
  FilterComparisonOperator,
} from "~/generated/graphql";
import { isEmpty, isNotEmpty } from "~/tools";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { AccountQuery } from "../types/AccountQuery";

export const prepareCloudAccountsFilterBy = (
  queryState: AccountQuery
): CloudAccountsFilterInput[] | undefined => {
  const cloudProvidersFilter = queryState.cloudProviders
    ? createFilterByInput<CloudAccountsFilterableFields>(
        CloudAccountsFilterableFields.Provider,
        queryState.cloudProviders,
        queryState.excludeCloudProviders ?? false
      )
    : [];

  const cloudAccountStatusesFilter = queryState.cloudAccountStatuses
    ? createFilterByInput<CloudAccountsFilterableFields>(
        CloudAccountsFilterableFields.Status,
        queryState.cloudAccountStatuses,
        queryState.excludeCloudAccountStatuses ?? false
      )
    : [];

  const dateAddedFilter =
    queryState.startDateUTC && queryState.endDateUTC
      ? [
          {
            field: CloudAccountsFilterableFields.CreatedAt,
            operator: FilterComparisonOperator.Ge,
            values: [queryState.startDateUTC],
          },
          {
            field: CloudAccountsFilterableFields.CreatedAt,
            operator: FilterComparisonOperator.Le,
            values: [queryState.endDateUTC],
          },
        ]
      : [];

  const retVal = [
    ...cloudProvidersFilter,
    ...cloudAccountStatusesFilter,
    ...dateAddedFilter,
  ];

  return isEmpty(retVal) ? undefined : retVal;
};

export const prepareCloudAccountsSearchFilter = (
  queryState: AccountQuery,
  phrase?: string
): CloudAccountsSearchInput | undefined => {
  const filterBy = prepareCloudAccountsFilterBy(queryState);
  const hasPhrase = phrase && isNotEmpty(phrase);

  if (!hasPhrase && !filterBy) {
    return undefined;
  }

  return {
    phrase: hasPhrase ? phrase : undefined,
    filterBy,
  };
};
